import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import "./RegisterStyle.css";

const ForgotPassword = () => {
  // Form state management
  const [email, setEmail] = useState("");

  // UI state management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");

  // Handle input change
  const handleChange = (e) => {
    setEmail(e.target.value);

    // Clear messages when user starts typing
    if (error || success) {
      setError("");
      setSuccess(false);
      setMessage("");
    }
  };

  // Form validation
  const validateForm = () => {
    if (!email.trim()) {
      setError("Please enter your email address");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setMessage("");

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/auth/forgot-password`,
        { email }
      );

      setSuccess(true);
      setMessage(
        response.data.message ||
        "Password reset instructions have been sent to your email address. Please check your inbox."
      );
      setEmail("");
    } catch (err) {
      setError(
        err.response?.data?.message ||
        "Failed to send reset email. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-container-main">
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-8 col-xl-7">
            <div className="row g-0 forgot-password-card-shadow forgot-password-rounded-modern overflow-hidden forgot-password-fade-in">
              {/* Left Side - Form */}
              <div className="col-md-7 bg-white p-5">
                <div className="forgot-password-form-header mb-4">
                  <h3 className="fw-bold mb-2">
                    <i className="fa fa-key forgot-password-header-icon me-2"></i>
                    Forgot Password?
                  </h3>
                  <p className="text-muted forgot-password-header-subtitle">
                    No worries! Enter your email and we'll send you reset instructions
                  </p>
                </div>

                {/* Error Alert */}
                {error && (
                  <div className="forgot-password-alert-danger mb-3">
                    <i className="fa fa-exclamation-circle me-2"></i>
                    <span>{error}</span>
                  </div>
                )}

                {/* Success Alert */}
                {success && (
                  <div className="forgot-password-alert-success mb-3">
                    <i className="fa fa-check-circle me-2"></i>
                    <span>{message}</span>
                  </div>
                )}

                {!success ? (
                  <form onSubmit={handleSubmit} noValidate>
                    {/* Email Field */}
                    <div className="mb-4">
                      <label className="forgot-password-form-label fw-semibold">
                        <i className="fa fa-envelope text-muted me-2"></i>
                        Email Address
                      </label>
                      <div className="forgot-password-input-group">
                        <input
                          type="email"
                          className="form-control forgot-password-input-modern"
                          name="email"
                          value={email}
                          onChange={handleChange}
                          placeholder="Enter your registered email"
                          required
                          autoComplete="email"
                          autoFocus
                        />
                      </div>
                      <small className="text-muted d-block mt-2">
                        <i className="fa fa-info-circle me-1"></i>
                        We'll send password reset instructions to this email
                      </small>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      className="btn forgot-password-btn-primary w-100 mb-3"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Sending Reset Link...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-paper-plane me-2"></i>
                          Send Reset Instructions
                        </>
                      )}
                    </button>

                    {/* Back to Login Link */}
                    <div className="text-center forgot-password-back-link">
                      <Link to="/login" className="forgot-password-link">
                        <i className="fa fa-arrow-left me-1"></i>
                        Back to Login
                      </Link>
                    </div>
                  </form>
                ) : (
                  <div className="forgot-password-success-content">
                    <div className="forgot-password-success-icon-wrapper mb-4">
                      <i className="fa fa-envelope-o forgot-password-success-icon"></i>
                    </div>
                    <p className="text-muted mb-4">
                      If an account exists with <strong>{email || "this email"}</strong>,
                      you will receive password reset instructions shortly.
                    </p>
                    <div className="forgot-password-next-steps mb-4">
                      <h6 className="fw-semibold mb-3">
                        <i className="fa fa-list-ol me-2"></i>
                        Next Steps:
                      </h6>
                      <ol className="forgot-password-steps-list">
                        <li>Check your email inbox</li>
                        <li>Click the reset link in the email</li>
                        <li>Create a new password</li>
                        <li>Sign in with your new password</li>
                      </ol>
                    </div>
                    <div className="d-grid gap-2">
                      <Link to="/login" className="btn forgot-password-btn-secondary">
                        <i className="fa fa-sign-in me-2"></i>
                        Return to Login
                      </Link>
                      <button
                        onClick={() => {
                          setSuccess(false);
                          setMessage("");
                        }}
                        className="btn forgot-password-btn-outline"
                      >
                        <i className="fa fa-refresh me-2"></i>
                        Send Another Email
                      </button>
                    </div>
                  </div>
                )}

                {/* Help Section */}
                <div className="forgot-password-help-section mt-4 pt-3 border-top">
                  <p className="text-muted small mb-2 text-center">
                    <i className="fa fa-question-circle me-1"></i>
                    Didn't receive the email?
                  </p>
                  <ul className="forgot-password-help-list small text-muted">
                    <li>Check your spam or junk folder</li>
                    <li>Make sure you entered the correct email</li>
                    <li>Wait a few minutes for the email to arrive</li>
                  </ul>
                </div>
              </div>

              {/* Right Side - Information */}
              <div className="col-md-5 forgot-password-gradient-bg text-white p-5 d-flex flex-column justify-content-between">
                <div>
                  <div className="forgot-password-brand-section mb-4">
                    <i className="fa fa-shield forgot-password-brand-icon mb-3"></i>
                    <h2 className="fw-bold mb-3 forgot-password-brand-title">
                      Secure Reset
                    </h2>
                    <p className="forgot-password-brand-description mb-4">
                      Your security is our priority. We use industry-standard encryption
                      to keep your account safe.
                    </p>
                  </div>

                  {/* Security Features */}
                  <div className="forgot-password-features-list">
                    <div className="forgot-password-feature-item mb-3">
                      <i className="fa fa-lock fa-lg me-3"></i>
                      <span>Encrypted reset links</span>
                    </div>
                    <div className="forgot-password-feature-item mb-3">
                      <i className="fa fa-clock-o fa-lg me-3"></i>
                      <span>Time-limited tokens</span>
                    </div>
                    <div className="forgot-password-feature-item mb-3">
                      <i className="fa fa-check-circle fa-lg me-3"></i>
                      <span>Email verification</span>
                    </div>
                    <div className="forgot-password-feature-item">
                      <i className="fa fa-user-secret fa-lg me-3"></i>
                      <span>Privacy protected</span>
                    </div>
                  </div>
                </div>

                {/* Contact Support */}
                <div className="forgot-password-support-section mt-4">
                  <div className="forgot-password-support-card">
                    <h6 className="fw-semibold mb-2">
                      <i className="fa fa-life-ring me-2"></i>
                      Need Help?
                    </h6>
                    <p className="small mb-2">
                      Contact our support team if you're having trouble
                    </p>
                    <a href="mailto:support@pdfapi.com" className="forgot-password-support-link">
                      <i className="fa fa-envelope me-1"></i>
                      support@pdfapi.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
